import { getScanFlags } from "@/domain/entities/ScanFlags";
import { AxiosResponse } from "axios";
import {
  CalculateVolume,
  CalculateData,
  CargoV2Data,
  CargoV2Entities,
  CargoV2HistoryData,
  CargoV2ProductData,
  CreateCargoV3,
  RtcData,
  RtcListEntities,
  CargoTransportType,
  ScanDetail,
  CheckEligibleReadyToCargoV2,
  BagOrSttDetail,
  CargoV2AircraftInfo
} from "@/domain/entities/CargoV2";
import {
  CargoDetailSttPiece,
  CargoDetailBagData,
  CargoAddResponse,
  CargoCommodityList,
  CargoCommodityData
} from "@/domain/entities/Cargo";
import { FlightDetail } from "@/domain/entities/NGen";
import { Pagination, PaginationV2 } from "@/domain/entities/Pagination";
import {
  ListClientParentEntities,
  ListClientParentData,
  CargoV2FlightDetail,
  DimensionCustomData,
  CargoV2SummaryInProcessData,
  CargoV2DetailBagData,
  CargoV2DetailStt,
  DimensionData,
  CargoV2MinimumWeightSummaryData,
  CargoV2DisputeSummaryData,
  CargoV2SuccessSummaryData,
  CargoV2FailedUnread,
  AwbDetailData,
  FlightData,
  CargoV2SearchFlightResult,
  CargoV2SearchFlight,
  CargoV2SearchFlightItinerary
} from "@/domain/entities/CargoV2";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
import { AwbDetail } from "@/domain/entities/NGen";
import {
  firstCapitalize,
  NGEN_BOOKING
} from "@/app/infrastructures/misc/Utils";
import startCase from "lodash/startCase";

function parseRcRemarks(remarks: string): Record<string, unknown> {
  if (!remarks || typeof remarks !== "string") return {};
  try {
    return (JSON.parse(remarks) as Record<string, unknown>) ?? {};
  } catch {
    return {};
  }
}

const rtcKeyName: any = {
  list: "config_cargo_flight",
  nextConfig: "next_config_cargo_flight",
  detail: "rtc_flight_config"
};

const _mapRtcAirportCodeOrigin = (data: any, key: any) => {
  return (
    data[rtcKeyName[key]]?.cargo_flight_origin ||
    data?.next_config_cargo_flight?.cargo_flight_origin ||
    data?.next_rtc_flight_config?.cargo_flight_origin ||
    ""
  );
};

const _mapRtcAirportCodeDestination = (data: any, key: any) => {
  return (
    data[rtcKeyName[key]]?.cargo_flight_destination ||
    data?.next_config_cargo_flight?.cargo_flight_destination ||
    data?.next_rtc_flight_config?.cargo_flight_destination ||
    ""
  );
};

const getRtcFlightData = (data: any, key: string): FlightDetail[] => {
  const flightsData: FlightDetail[] = [
    new FlightDetail({
      DailyFlightSNo: 0,
      FlightNo: data[rtcKeyName[key]]?.cargo_flight_number || "",
      flightDayTransit: data[rtcKeyName[key]]?.cargo_flight_day_transit || 0,
      departureHour: data[rtcKeyName[key]]?.cargo_flight_departure_hour || "",
      arrivalHour: data[rtcKeyName[key]]?.cargo_flight_arrival_hour || "",
      airportCodeOrigin: _mapRtcAirportCodeOrigin(data, key) || "",
      airportCodeDestination: _mapRtcAirportCodeDestination(data, key) || "",
      origin: `${data.rtc_origin_code || ""} - ${firstCapitalize(
        data.rtc_origin_name?.toLowerCase()
      )}`,
      thresholdTime: data[rtcKeyName[key]]?.cargo_flight_treshold || 0
    })
  ];

  if (data[rtcKeyName[key]]?.cargo_flight_transit?.length) {
    data[rtcKeyName[key]]?.cargo_flight_transit.forEach(
      (transit: any, index: number) => {
        flightsData.push(
          new FlightDetail({
            DailyFlightSNo: index + 1,
            FlightNo: transit?.cargo_flight_number || "",
            flightDayTransit: transit?.cargo_flight_day_transit || 0,
            departureHour: transit?.cargo_flight_departure_hour || "",
            arrivalHour: transit?.cargo_flight_arrival_hour || "",
            airportCodeOrigin: transit?.cargo_flight_origin || "",
            airportCodeDestination: transit?.cargo_flight_destination || "",
            destination:
              index === data[rtcKeyName[key]]?.cargo_flight_transit?.length - 1
                ? `${data.rtc_destination_code} - ${firstCapitalize(
                    data.rtc_destination_name?.toLowerCase()
                  )}`
                : "",
            thresholdTime: transit.cargo_flight_treshold || 0
          })
        );
      }
    );
  } else {
    flightsData[0].destination = `${
      data.rtc_destination_code
    } - ${firstCapitalize(data.rtc_destination_name?.toLowerCase())}`;
  }
  return flightsData;
};

const getRtcNextFlightsData = (data: any): FlightDetail[] => {
  const config = data?.next_rtc_flight_config ?? {};

  return [
    new FlightDetail({
      DailyFlightSNo: 0,
      FlightNo: config.cargo_flight_number || "",
      flightDayTransit: config.cargo_flight_day_transit || 0,
      departureHour: config.cargo_flight_departure_hour || "",
      arrivalHour: config.cargo_flight_arrival_hour || "",
      airportCodeOrigin: config.cargo_flight_origin || "",
      airportCodeDestination: config.cargo_flight_destination || "",
      thresholdTime: config.cargo_flight_treshold
    })
  ];
};

const mappingRtcStatus = (status: any) => {
  if (status.toLowerCase() === "sudah bisa") {
    return "Bisa Dikargo";
  }
  if (status.toLowerCase() === "sudah siap") {
    return "Harus Dikargo";
  }
  if (status.toLowerCase() === "belum bisa") {
    return "Belum Siap Dikargo";
  }
  return status.toLowerCase();
};

/** Maps one list item from GET /hydra/v3/cargo/list (success tab, cargo_*) to CargoV2Data. */
function mapCargoV2ListItemFromApi(item: Record<string, any>): CargoV2Data {
  return new CargoV2Data({
    ...convertSnakeToCamel(item),
    totalFlight: item.total_flight,
    id: item.cargo_id,
    cargoNumber: item.cargo_no,
    cargoHubName: item.cargo_hub_name,
    cargoType: item.cargo_type,
    vehiclePoliceNumber: item.cargo_vehicle_no,
    originCityCode: item.cargo_origin_city_code,
    originCityName: item.origin_city_name,
    destinationCityCode: item.cargo_destination_city_code,
    destinationCityName: item.destination_city_name,
    airportOriginCityCode: item.cargo_origin_airport_code,
    airportOriginCityName: item.airport_origin_name,
    airportDestinationCityCode: item.cargo_destination_airport_code,
    airportDestinationCityName: item.airport_destination_name,
    originDistrictCode: item.cargo_origin_district_code,
    destinationDistrictCode: item.cargo_destination_district_code,
    totalStt: item.cargo_total_stt,
    totalPieces: item.cargo_total_stt_piece,
    totalGrossWeight: item.cargo_total_gross_weight,
    totalVolumeWeight: item.cargo_total_volume_weight,
    noBag: item.cargo_number,
    createdName: item.created_name,
    updatedByName: item.updated_by_name,
    totalEstCargoGrossWeight: 0,
    totalEstCargoVolumeWeight: 0,
    totalActualCargoGrossWeight: item.cargo_total_custom_gross_weight,
    totalActualCargoVolumeWeight: item.cargo_total_custom_volume_weight,
    cargoActualPiece: item.cargo_actual_piece,
    partnerName: "",
    partnerType: "",
    cargoCommodity: "",
    cargoCommodityCode: "",
    cargoProduct: "",
    cargoProductCode: "",
    estimationDepartureData: "",
    estimationDepartureDate: item.cargo_estimation_departure_date,
    departureDate: item.cargo_departure_date,
    departureTime: item.cargo_departure_ngen,
    estimationArrivalDate: item.cargo_estimation_arrival_date,
    arrivalDate: item.cargo_arrival_date,
    arrivalTime: item.cargo_arrival_ngen,
    createdAt: item.cargo_created_at,
    createdBy: item.cargo_created_name,
    updatedAt: item.cargo_updated_at,
    updatedBy: item.cargo_updated_name,
    detailStt: [],
    totalPiecesBagStt: 0,
    actualDimension: new DimensionData(),
    transit: item.total_transit,
    cargoDimensionCustom: [],
    isPlaneManual: item.cargo_is_cargo_plane_manual,
    totalPiecesDimension: 0,
    totalGrossDimension: 0,
    totalChargeableWeight: item.cargo_total_chargeable_weight,
    cargoTotalCustomChargeableWeight: item.cargo_total_custom_chargeable_weight,
    cargoTotalChargeableWeight: item.cargo_total_chargeable_weight,
    cargoTotalCustomGrossWeight: item.cargo_total_custom_gross_weight,
    cargoTotalCustomVolumeWeight: item.cargo_total_custom_volume_weight,
    cargoTotalEstimateChargeableWeight:
      item.cargo_total_estimate_chargeable_weight,
    cargoTotalEstimateGrossWeight: item.cargo_total_estimate_gross_weight,
    cargoTotalEstimateVolumeWeight: item.cargo_total_estimate_volume_weight,
    cargoTotalGrossWeight: item.cargo_total_gross_weight,
    cargoTotalVolumeWeight: item.cargo_total_volume_weight,
    isDisputeCargo: item.is_dispute_cargo,
    cargoCommodityGroupIsElectronicDg:
      item.cargo_commodity_group_is_electronic_dg,
    cargoCommodityGroupIsDg: item.cargo_commodity_group_is_dg,
    cargoCommodityGroupIsQuarantine: item.cargo_commodity_group_is_quarantine,
    isDisputeFromBagging: item.is_dispute_from_bagging,
    isDisputeFromCargo: item.is_dispute_from_cargo,
    isNewForm: item.is_new_form
  });
}

/** Maps one list item from GET /hydra/v3/retry_cargo (rc_*) to CargoV2Data. */
function mapCargoV2ListInProgressItemFromApi(
  item: Record<string, any>
): CargoV2Data {
  return new CargoV2Data({
    totalFlight: item.rc_total_flight,
    id: item.rc_id,
    cargoNumber: item.rc_no,
    cargoHubName: item.rc_hub_name,
    estimationDepartureDate: item.rc_departure_date,
    estimationArrivalDate: item.rc_arrival_date,
    bookingRequestId: item.rc_booking_request_id,
    isFromRtc: item.is_from_rtc,
    cargoType: item.rc_type,
    vehiclePoliceNumber: item.rc_vehicle_no,
    originCityCode: item.rc_origin_city_code,
    originCityName: JSON.parse(item.rc_remarks).origin_city_name,
    destinationCityCode: item.rc_destination_city_code,
    destinationCityName: JSON.parse(item.rc_remarks).destination_city_name,
    airportOriginCityCode: item.rc_origin_airport_code,
    airportOriginCityName: JSON.parse(item.rc_remarks).airport_origin_name,
    airportDestinationCityCode: item.rc_destination_airport_code,
    airportDestinationCityName: JSON.parse(item.rc_remarks)
      .airport_destination_name,
    originDistrictCode: item.rc_origin_district_code ?? "",
    destinationDistrictCode: item.rc_destination_district_code ?? "",
    totalStt: item.rc_total_stt,
    totalPieces: item.rc_total_stt_piece,
    totalGrossWeight: item.rc_total_gross_weight,
    totalVolumeWeight: item.rc_total_volume_weight,
    totalEstCargoGrossWeight: 0,
    totalEstCargoVolumeWeight: 0,
    totalActualCargoGrossWeight: item.rc_total_custom_gross_weight,
    totalActualCargoVolumeWeight: item.rc_total_custom_volume_weight,
    cargoActualPiece: item.rc_cargo_actual_piece || item.rc_actual_piece,
    partnerName: "",
    partnerType: "",
    cargoCommodity: "",
    cargoCommodityCode: "",
    cargoProduct: "",
    cargoProductCode: "",
    estimationDepartureData: "",
    departureDate: item.rc_departure_date,
    arrivalDate: item.rc_arrival_date,
    createdAt: item.rc_created_at,
    createdBy: item.rc_created_name,
    updatedAt: item.rc_updated_at,
    updatedBy: item.rc_updated_name,
    detailStt: [],
    totalPiecesBagStt: 0,
    actualDimension: new DimensionData(),
    reasonFailed: item.rc_reason_failed,
    cargoDimensionCustom: [],
    isPlaneManual: item.rc_is_cargo_plane_manual,
    totalPiecesDimension: 0,
    totalGrossDimension: 0,
    totalChargeableWeight: item.rc_total_chargeable_weight,
    cargoTotalCustomChargeableWeight: item.rc_total_custom_chargeable_weight,
    isDisputeFromBagging: item.is_dispute_from_bagging,
    isDisputeFromCargo: item.is_dispute_from_cargo,
    cargoStatus: item.rc_status
  });
}

/** Maps one list item from GET /hydra/v3/retry_cargo (status cancel/failed, rc_*) to CargoV2Data. */
function mapCargoV2ListCancelledItemFromApi(
  item: Record<string, unknown>
): CargoV2Data {
  const base = mapCargoV2ListInProgressItemFromApi(item);
  const status = String(item.rc_status ?? "").toLowerCase();
  const reason = String(item.rc_reason_failed ?? "").trim();
  return new CargoV2Data({
    ...((base as unknown) as Partial<CargoV2Data>),
    cargoStatus: status,
    reasonFailed: reason || "Belum tersedia"
  });
}

export class CargoV2Mapper {
  public convertListFromApi(
    res: AxiosResponse<{
      meta?: { page?: number; limit?: number; total_data?: number };
      data?: Record<string, unknown>[];
    }>
  ): CargoV2Entities {
    const body = res.data;
    const meta = body?.meta ?? {};
    const page = Number(meta.page) || 1;
    const limit = Number(meta.limit) || 20;
    const totalRecords = Number(meta.total_data) ?? 0;
    const rawList = Array.isArray(body?.data) ? body.data : [];
    const data = rawList.map((item) =>
      mapCargoV2ListItemFromApi(item as Record<string, unknown>)
    );
    return new CargoV2Entities({
      pagination: new Pagination(page, limit, totalRecords),
      data
    });
  }

  /** Converts GET /hydra/v3/retry_cargo response (rc_*) to CargoV2Entities. */
  public convertListInProgressFromApi(
    res: AxiosResponse<{
      meta?: { page?: number; limit?: number; total_data?: number };
      data?: Record<string, unknown>[];
    }>
  ): CargoV2Entities {
    const body = res.data;
    const meta = body?.meta ?? {};
    const page = Number(meta.page) || 1;
    const limit = Number(meta.limit) || 20;
    const totalRecords = Number(meta.total_data) ?? 0;
    const rawList = Array.isArray(body?.data) ? body.data : [];
    const data = rawList.map((item) =>
      mapCargoV2ListInProgressItemFromApi(item as Record<string, unknown>)
    );
    return new CargoV2Entities({
      pagination: new Pagination(page, limit, totalRecords),
      data
    });
  }

  /** Converts GET /hydra/v3/retry_cargo (status cancel/failed) response to CargoV2Entities. */
  public convertListCancelledFromApi(
    res: AxiosResponse<{
      meta?: { page?: number; limit?: number; total_data?: number };
      data?: Record<string, unknown>[];
    }>
  ): CargoV2Entities {
    const body = res.data;
    const meta = body?.meta ?? {};
    const page = Number(meta.page) || 1;
    const limit = Number(meta.limit) || 20;
    const totalRecords = Number(meta.total_data) ?? 0;
    const rawList = Array.isArray(body?.data) ? body.data : [];
    const data = rawList.map((item) =>
      mapCargoV2ListCancelledItemFromApi(item as Record<string, unknown>)
    );
    return new CargoV2Entities({
      pagination: new Pagination(page, limit, totalRecords),
      data
    });
  }

  public convertDetailFromApi(res: AxiosResponse<any>): CargoV2Data {
    const data = res.data;

    console.log(data);

    return new CargoV2Data();
  }

  public convertCargoSuccessSummaryDataFromApi(
    res: AxiosResponse<{
      data?: { plane?: number; truck?: number; ship?: number };
    }>
  ): CargoV2SuccessSummaryData {
    const dataRes = res.data?.data ?? {};
    return new CargoV2SuccessSummaryData({
      plane: String(dataRes.plane ?? 0),
      truck: String(dataRes.truck ?? 0),
      ship: String(dataRes.ship ?? 0)
    });
  }

  public convertCargoDisputeSummaryDataFromApi(
    res: AxiosResponse<any>
  ): CargoV2DisputeSummaryData {
    const data = res.data?.data ?? {};
    return new CargoV2DisputeSummaryData(convertSnakeToCamel(data));
  }

  public convertCargoMinimumWeightSummaryFromApi(
    res: AxiosResponse<any>
  ): CargoV2MinimumWeightSummaryData {
    const data = res.data?.data ?? {};
    return new CargoV2MinimumWeightSummaryData({
      totalData: data.minimum_volume_total ?? 0,
      totalMinimumVolume: data.minimum_volume_count ?? 0,
      totalPercentageMinimumVolume: data.minimum_volume_percentage ?? 0,
      startDate: data.start_date ?? "",
      endDate: data.end_date ?? ""
    });
  }

  public convertPayloadData(result: AxiosResponse<any>): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data?.success,
      message: data?.message ?? ""
    });
  }

  /** Converts GET /hydra/v3/ready-to-cargo response to RtcListEntities. */
  public convertRtcListFromApi(res: AxiosResponse): RtcListEntities {
    const { data } = res;

    return new RtcListEntities({
      pagination: new Pagination(
        data.Meta.page,
        data.Meta.limit,
        data.Meta.total_records
      ),
      data: data.Data.length
        ? data.Data.map(
            (item: any) =>
              new RtcData({
                ...convertSnakeToCamel(item),
                id: item.rtc_id,
                noReadyToCargo: item.rtc_no,
                totalBagging: item.rtc_total_bagging,
                totalPieces: item.rtc_total_piece || 0,
                cargoType: item.rtc_cc_transport.toLowerCase(),
                totalWeight: item.rtc_total_weight,
                originCode: item.rtc_origin_code,
                originName: item.rtc_origin_name.toLowerCase() || "-",
                destinationCode: item.rtc_destination_code,
                destinationName: item.rtc_destination_name.toLowerCase() || "-",
                status: mappingRtcStatus(item.rtc_status),
                createdAt: item.rtc_created_at,
                createdName: item.rtc_created_name,
                updatedAt: item.rtc_updated_at,
                updateName: item.rtc_updated_name,
                hubName: item.rtc_hub_name,
                flightsData: this.mapRtcFlightData(item),
                nextConfigCargoFlight: this.mapRtcNextFlightData(item),
                nextCargoFlightTime: item.next_cargo_flight_time,
                haveConfigCargoFlight:
                  item.rtc_cc_transport.match(/plane/gi) &&
                  !!item.config_cargo_cut_off.ct_time,
                thresholdTime:
                  item.config_cargo_flight.cargo_flight_treshold || 0,
                cutOffTime: item.config_cargo_cut_off.ct_time,
                clientParentName: item.rtc_client_parent_name,
                isMinimumPiece: item.is_minimum_piece,
                isMinimumWeight: item.is_minimum_weight
              })
          )
        : []
    });
  }

  public convertAirportDataFromApi(
    result: AxiosResponse<any>
  ): ListClientParentEntities {
    const { data } = result;

    return new ListClientParentEntities({
      pagination: new PaginationV2({
        page: data.meta.page,
        limit: data.meta.limit
      }),
      data: data.data.length
        ? data.data.map(
            (item: any) =>
              new ListClientParentData({
                id: item.client_parent_id,
                name: item.client_parent_name
              })
          )
        : []
    });
  }

  private mapRtcFlightData(item: any) {
    return !item.config_cargo_cut_off.ct_time &&
      item.next_config_cargo_flight?.cargo_flight_departure_hour
      ? getRtcFlightData(item, "nextConfig")
      : getRtcFlightData(item, "list");
  }

  private mapRtcNextFlightData(item: any) {
    return !item.config_cargo_cut_off.ct_time &&
      item.next_config_cargo_flight?.cargo_flight_departure_hour
      ? getRtcFlightData(item, "nextConfig")
      : [new FlightDetail()];
  }

  private _mapBagOrSttRTC(data: any) {
    return Array.isArray(data.bag_or_stt)
      ? data.bag_or_stt.map((key: any) => {
          const sttPieces = key?.stt_pieces?.length ? key.stt_pieces : [];
          return new CargoV2DetailStt({
            baggingNumber: key.bag_no,
            sttNumber: key.stt_no,
            sttElexysNumber: key.stt_no_elexys,
            totalPieces: key.stt_total_piece,
            grossWeigth: key.gross_weight,
            volumeWeight: key.volume_weight,
            productType: key.product_type,
            commodity: key.commodity_name,
            destinationCityCode: data.rtc_destination_code,
            destinationCityName: startCase(
              data.rtc_destination_name.toLowerCase()
            ),
            commodityGroupCode: key.commodity_group_code,
            commodityGroupName: key.commodity_group_name,
            commodityGroupIsDg: key.commodity_group_is_dg,
            commodityGroupIsQuarantine: key.commodity_group_is_quarantine,
            regionId: key.region_id,
            regionName: key.region_name,
            bagCustomGrossWeight: key.bag_custom_gross_weight,
            sttNoRefExternal: key.stt_no_ref_external,
            shcCode: key.rtc_shc_code,
            shcName: key.rtc_shc_description,
            bagGrossWeight: key.bag_custom_gross_weight,
            bagCustomVolumeWeight: key.bag_custom_volume_weight,
            bagCustomHeight: key.bag_custom_height,
            bagCustomWidth: key.bag_custom_width,
            bagCustomLength: key.bag_custom_length,
            grossWeight: key.gross_weight,
            customGrossWeight: key.custom_gross_weight,
            chargeableWeight: key.chargeable_weight,
            customChargeableWeight: key.custom_chargeable_weight,
            customVolumeWeight: key.custom_volume_weight,
            bagTotalChargeableWeight: key.bag_total_chargeable_weight,
            bagCustomChargeableWeight: key.bag_total_chargeable_weight,
            sttPieces:
              key.stt_pieces?.map(
                (piece: any) =>
                  new CargoDetailSttPiece({
                    sttPieceLength: piece.stt_piece_length,
                    sttPieceWidth: piece.stt_piece_width,
                    sttPieceHeight: piece.stt_piece_height,
                    sttPieceGrossWeight: piece.stt_piece_gross_weight,
                    sttPieceVolumeWeight: piece.stt_piece_volume_weight
                  })
              ) ?? [],
            bagData: new CargoDetailBagData({
              bagCustomGrossWeight: key.bag_custom_gross_weight,
              bagCustomLength: key.bag_custom_length,
              bagCustomWidth: key.bag_custom_width,
              bagCustomHeight: key.bag_custom_height,
              bagCustomChargeableWeight: key.bag_total_chargeable_weight,
              isUseNewCalculation: data.is_use_new_calculation,
              bagGrossWeight: key.gross_weight,
              bagTotalChargeableWeight: key.bag_total_chargeable_weight,
              totalSttChargeableWeight: key.total_stt_chargeable_weight,
              bagCustomVolumeWeight: key.bag_custom_volume_weight
            })
          });
        })
      : [];
  }

  private _mapBagOrSttRTCV2(data: any) {
    return Array.isArray(data.bag_or_stt)
      ? data.bag_or_stt.map((item: any) => {
          return new BagOrSttDetail({
            bagData: new CargoV2DetailBagData({
              bagTotalChargeableWeight: item.bag_total_chargeable_weight,
              bagCustomChargeableWeight: item.bag_total_chargeable_weight,
              bagCustomGrossWeight: item.bag_custom_gross_weight,
              bagCustomVolumeWeight: item.bag_custom_volume_weight
            }),
            bagNo: item.bag_no,
            sttNo: item.stt_no,
            volumeWeight: item.volume_weight,
            chargeableWeight: item.chargeable_weight,
            commodityCode: item.commodity_code,
            commodityGroupCode: item.commodity_group_code,
            commodityGroupIsDg: item.commodity_group_is_dg,
            commodityGroupName: item.commodity_group_name,
            commodityGroupIsQuarantine: item.commodity_group_is_quarantine,
            commodityId: item.commodity_id,
            commodityName: item.commodity_name,
            destinationCityCode: item.destination_city_code,
            destinationCityName: item.destination_city_name,
            destinationDistrictCode: item.destination_district_code,
            grossWeight: item.gross_weight,
            isDangerousGoods: item.is_dangerous_goods,
            isQuarantine: item.is_quarantine,
            isPaid: item.is_paid,
            productType: item.product_type,
            regionId: item.region_id,
            regionName: item.region_name,
            shcCode: item.rtc_shc_code,
            shcDescription: item.rtc_shc_description,
            shipmentId: item.stt_shipment_id,
            sttDetails: [],
            sttId: item.stt_id,
            sttNoElexys: item.stt_no_elexys,
            sttPieces:
              item.stt_pieces?.map(
                (piece: any) =>
                  new CargoDetailSttPiece({
                    sttPieceLength: piece.stt_piece_length,
                    sttPieceWidth: piece.stt_piece_width,
                    sttPieceHeight: piece.stt_piece_height,
                    sttPieceGrossWeight: piece.stt_piece_gross_weight,
                    sttPieceVolumeWeight: piece.stt_piece_volume_weight
                  })
              ) ?? [],
            sttTotalPiece: item.stt_total_piece,
            sttClientId: item.stt_client_id
          });
        })
      : [];
  }

  public convertRtcDetailFromApi(res: AxiosResponse): RtcData {
    const {
      data: { data }
    } = res;

    return new RtcData({
      id: data.rtc_id,
      noReadyToCargo: data.rtc_no,
      totalBagging: data.rtc_total_bagging,
      totalWeight: data.rtc_total_weight,
      totalPieces: data.rtc_total_piece,
      originCode: data.rtc_origin_code,
      originName: startCase(data.rtc_origin_name.toLowerCase()) || "-",
      cargoType: data.rtc_cc_transport.toLowerCase(),
      commodityCode: data.rtc_cc_comodity_group_code,
      commodityName: data.rtc_cc_comodity_group_name,
      destinationCode: data.rtc_destination_code,
      destinationName:
        startCase(data.rtc_destination_name.toLowerCase()) || "-",
      destinationCountryCode: data.destination_country_code || "",
      status:
        data.rtc_status.toLowerCase() === "sudah bisa"
          ? "Bisa Dikargo"
          : "Harus Dikargo",
      productType: data.rtc_cc_product_type,
      detail: this._mapBagOrSttRTC(data),
      flightsData: getRtcFlightData(data, "detail"),
      nextFlightsData: getRtcNextFlightsData(data),
      cutOffTime: data.rtc_cut_off_time.ct_time,
      thresholdTime: data.rtc_flight_config.cargo_flight_treshold || 0,
      nextCargoFlightTime: data.next_rtc_flight_time || "",
      isDangerousGoods: data.is_dangerous_goods || false,
      isQuarantine: data.is_quarantine || false,
      isBosspack: data.is_bosspack || false,
      totalSttGrossWeight: data.total_stt_gross_weight,
      totalSttVolumeWeight: data.total_stt_volume_weight,
      totalSttChargeableWeight: data.total_stt_chargeable_weight,
      isUseNewCalculation: data.is_use_new_calculation,
      rtcCargoProductType:
        data.rtc_cargo_product_type || NGEN_BOOKING.PRODUCT_NAME,
      bagOrStt: this._mapBagOrSttRTCV2(data),
      rtcCcFlight: Array.isArray(data.rtc_cc_flight)
        ? JSON.parse(data.rtc_cc_flight)
        : [],
      nextRtcFlightConfig: data?.next_rtc_flight_config
    });
  }

  public convertCheckEligibleRtcFromApi(
    res: AxiosResponse
  ): CheckEligibleReadyToCargoV2 {
    return new CheckEligibleReadyToCargoV2(convertSnakeToCamel(res.data.data));
  }

  public convertCargoDetailFromApi(result: AxiosResponse<any>): CargoV2Data {
    const { data } = result.data;
    return this.createCargoDataFromApiData(data);
  }

  private createCargoDataFromApiData(data: any): CargoV2Data {
    // sini
    return new CargoV2Data({
      id: data.cargo_id,
      cargoNumber: data.cargo_number,
      cargoType: data.cargo_type,
      vehiclePoliceNumber: data.vehicle_number,
      driverName: data.driver_name,
      originCityCode: data.origin_city_code,
      originCityName: data.origin_city,
      originCountryCode: data.origin_country_code || "",
      destinationCityCode: data.destination_city_code,
      destinationCityName: data.destination_city,
      destinationCountryCode: data.destination_country_code || "",
      airportOriginCityCode: data.origin_airport_code,
      airportOriginCityName: data.origin_airport_name,
      airportDestinationCityCode: data.destination_airport_code,
      airportDestinationCityName: data.destination_airport_name,
      originDistrictCode: "",
      destinationDistrictCode: "",
      totalStt: data.total_stt,
      totalPieces: data.total_piece,
      totalGrossWeight: data.total_gross_weight,
      totalVolumeWeight: data.total_volume_weight,
      noBag: data.cargo_number,
      createdName: data.created_name,
      updatedByName: data.updated_by_name,
      totalEstCargoGrossWeight: data.total_est_cargo_gross_weight,
      totalEstCargoVolumeWeight: data.total_est_cargo_volume_weight,
      totalActualCargoGrossWeight: data.total_actual_cargo_gross_weight,
      totalActualCargoVolumeWeight: data.total_actual_cargo_volume_weight,
      cargoActualPiece: data.cargo_actual_piece,
      partnerName: data.partner_name,
      partnerType: data.partner_type,
      cargoCommodity: data.cargo_commodity,
      cargoCommodityCode: data.cargo_commodity_code,
      cargoProduct: data.cargo_product,
      cargoProductCode: data.cargo_product_code,
      shcCode: data.shc_code,
      shcDescription: data.shc_description,
      estimationDepartureData: data.estimation_departure_data,
      departureDate: data.departure_date,
      arrivalDate: data.arrival_date || data.aririval_date,
      createdAt: data.created_at,
      createdBy: data.created_name,
      updatedAt: data.updated_at,
      updatedBy: data.update_by,
      isFlightVia: data.is_flight_via ?? false,
      isTransit: data.is_transit ?? false,
      detailStt: this.createCargoV2DetailStts(data.detail_stt),
      flightsDetail: this.createFlightsDetail(data.cargo_flight_details),
      cargoLabelFlightSegments: this.createFlightsDetail(
        data.cargo_label_flight_segments
      ),
      totalPiecesBagStt: data.detail_stt
        ? data.detail_stt.reduce((a: any, b: any) => a + b.TotalPiece, 0)
        : 0,
      totalPiecesDimension: this.calculateTotalPiecesDimension(
        this.mapCargoDimensionCustom(data.cargo_dimension_custom)
      ),
      totalGrossDimension: this.calculateTotalGrossDimension(
        this.mapCargoDimensionCustom(data.cargo_dimension_custom)
      ),
      actualDimension: this.createDimensionData(data, "actual"),
      estDimension: this.createDimensionData(data, "est"),
      nog: data.cargo_nog,
      partnerId: data.partner_id || 0,
      flightNumbers: data.flight_numbers,
      cargoDimensionCustom: this.mapCargoDimensionCustom(
        data.cargo_dimension_custom
      ),
      nGenCreatedAt: data.ngen_created_at,
      isPlaneManual: data.cargo_is_cargo_plane_manual,
      photoProof: data.photo_proof,
      isAllowUseProof: data.is_allow_use_proof,
      isUseNewCalculation: data.is_use_new_calculation,
      totalChargeableWeight: data.cargo_total_chargeable_weight,
      rcTotalChargeableWeight: data.rc_total_chargeable_weight,
      rcTotalCustomChargeableWeight: data.rc_total_custom_chargeable_weight,
      rcTotalEstimateChargeableWeight: data.rc_total_estimate_chargeable_weight,
      cargoTotalChargeableWeight: data.cargo_total_chargeable_weight,
      cargoTotalEstimateChargeableWeight:
        data.cargo_total_estimate_chargeable_weight,
      cargoTotalCustomChargeableWeight:
        data.cargo_total_custom_chargeable_weight,
      calculateType: data.calculate_type,
      isMinimumVolumePerPiece: data.is_minimum_volume_per_piece,
      minVolumePerPiece: data.min_volume_per_piece,
      isValidAwbManual: data.is_valid_awb_manual,
      isCargoDimensionOn: data.is_cargo_dimension_on,
      isDisputeCargo: data.is_dispute_cargo,
      cargoCommodityGroupIsElectronicDg:
        data.cargo_commodity_group_is_electronic_dg,
      cargoCommodityGroupIsDg: data.cargo_commodity_group_is_dg,
      cargoCommodityGroupIsQuarantine: data.cargo_commodity_group_is_quarantine,
      totalDisputeBagging: data.total_bag_with_weight_disputtotalBagginge || 0,
      totalBagging: data.total_bag || 0,
      isPrintManifest: data.is_print_manifest,
      isPrintLabel: data.is_print_label,
      isPrintPti: data.is_print_pti,
      isPrintSptj: data.is_print_sptj,
      isPrintAwb: data.is_print_awb,
      trackingStatus: data.tracking_status,
      cargoNog: data.cargo_nog,
      totalBagWithWeightDispute: data.total_bag_with_weight_dispute,
      isNewForm: data.is_new_form,
      isDisputeFromBagging: data.is_dispute_from_bagging,
      isDisputeFromCargo: data.is_dispute_from_cargo,
      onlyTotalBag: data.only_total_bag,
      onlyTotalStt: data.only_total_stt,
      currentChargeableWeight: data.current_chargeable_weight
    });
  }

  private mapCargoDimensionCustom(data: any): DimensionCustomData[] {
    return (
      data?.map(
        (item: any) =>
          new DimensionCustomData({
            pieces: item.pieces,
            length: item.length,
            width: item.width,
            height: item.height,
            grossWeight: item.gross_weight,
            volumeWeight: item.volume_weight,
            sttOrBag: item.stt_or_bag
          })
      ) || []
    );
  }

  private createDimensionData(
    data: any,
    type: "actual" | "est"
  ): DimensionData {
    return new DimensionData({
      length: data[`cargo_${type}_length_dimension`],
      width: data[`cargo_${type}_width_dimension`],
      height: data[`cargo_${type}_height_dimension`]
    });
  }

  private calculateTotalGrossDimension(detail: DimensionCustomData[]): number {
    return detail.reduce(
      (a: number, b: DimensionCustomData) => a + b.grossWeight,
      0
    );
  }

  private calculateTotalPiecesDimension(detail: DimensionCustomData[]): number {
    return detail.reduce(
      (a: number, b: DimensionCustomData) => a + b.pieces,
      0
    );
  }

  private createFlightsDetail(flightDetails: any[]): CargoV2FlightDetail[] {
    return (
      flightDetails?.map((item: any) => {
        return new CargoV2FlightDetail({
          cfNumber: item.cf_number,
          cfEstDepartureTime: item.cf_est_departure_time,
          cfAirportOrigin: item.cf_airport_origin,
          cfAirportDestination: item.cf_airport_destination,
          cfEstArrivalTime: item.cf_est_arrival_time,
          isFlightVia: Boolean(item?.is_flight_via),
          isTransit: Boolean(item?.is_transit)
        });
      }) || []
    );
  }

  public convertCargoSummaryInProcess(
    res: AxiosResponse
  ): CargoV2SummaryInProcessData {
    const {
      data: { data }
    } = res;

    return new CargoV2SummaryInProcessData({
      inProcessTotal: data.in_process_total
    });
  }

  private createCargoV2DetailStts(detailStt: any): CargoV2DetailStt[] {
    return detailStt
      ? detailStt.map(
          (stt: any) =>
            new CargoV2DetailStt({
              baggingNumber: stt.bag_no,
              baggingVendorNumber: stt.bag_vendor_no,
              sttNumber: stt.stt_no,
              totalPieces: stt.stt_total_piece,
              grossWeigth: stt.stt_gross_weight,
              volumeWeight: stt.stt_volume_weight,
              productType: stt.stt_product_type,
              commodity: stt.stt_commodity_name,
              origin: stt.stt_origin_city_id,
              destination: stt.stt_destination_city_id,
              sttTotalPiece: stt.stt_total_piece,
              sttGrossWeight: stt.stt_gross_weight,
              sttVolumeWeight: stt.stt_volume_weight,
              sttProductType: stt.stt_product_type,
              sttCommodityName: stt.stt_commodity_name,
              sttOriginCityId: stt.stt_origin_city_id,
              sttDestinationCityId: stt.stt_destination_city_id,
              sttNoRefExternal: stt.stt_no_ref_external,
              isScanByBag: stt.is_scan_by_bag,
              bagData: new CargoV2DetailBagData({
                bagCustomGrossWeight: stt.bag_custom_gross_weight,
                bagCustomVolumeWeight: stt.bag_custom_volume_weight,
                bagCustomChargeableWeight: stt.bag_custom_chargeable_weight
              }),
              chargeableWeight: stt.stt_chargeable_weight,
              bagCustomChargeableWeight: stt.bag_custom_chargeable_weight,
              isDisputeBagging: stt.is_dispute_bagging,
              isDangerousGoods: stt.is_dangerous_goods,
              isQuarantine: stt.is_quarantine,
              shipmentId: stt.stt_shipment_id,
              bagPhotoProof: stt.bag_photo_proof || [],
              sttClientId: stt.stt_client_id
            })
        )
      : [];
  }

  public convertLastIdCargoAndRetryCargo(result: AxiosResponse): number {
    const {
      data: { data }
    } = result;
    return data.last_id;
  }

  public convertCargoFailedUnreadFromApi(
    res: AxiosResponse
  ): CargoV2FailedUnread {
    const {
      data: { data }
    } = res;
    return new CargoV2FailedUnread({
      isShow: data.is_show,
      newRcId: data.new_rc_id
    });
  }

  /** Converts POST /hydra/v3/cargo/search-flight response to CargoV2SearchFlightResult. */
  public convertSearchFlightFromApi(
    result: AxiosResponse<any>
  ): CargoV2SearchFlightResult {
    const { data } = result;
    const totalFlight = Number(data?.total_flight ?? 0);
    const rawList = Array.isArray(data?.data) ? data.data : [];
    const flights = rawList.map((item: any) => {
      const itineraries = (item.itineraries ?? []).map(
        (it: any) =>
          new CargoV2SearchFlightItinerary({
            dailyFlightNo: it.daily_flight_no ?? "",
            flightNo: it.flight_no ?? "",
            departureDate: it.departure_date ?? "",
            arrivalDate: it.arrival_date ?? "",
            originAirportCode: it.origin_airport_code ?? "",
            destinationAirportCode: it.destination_airport_code ?? "",
            isDgAllowed: Boolean(it.is_dg_allowed),
            capacityAvailable: Boolean(it.capacity_available),
            dimensionAvailable: Boolean(it.dimension_available),
            historyDepartureStatus: it.history_departure_status ?? "",
            embargo: Boolean(it.embargo),
            remainingCapacity: it.remaining_capacity ?? 0,
            remainingCapacityAllotment: it.remaining_capacity_allotment ?? 0,
            remainingCapacityFreesales: it.remaining_capacity_freesales ?? 0,
            totalTravelTimeMin: it.total_travel_time_min ?? 0,
            aircraftInfo: new CargoV2AircraftInfo({
              manufacturer: it.aircraft_info?.manufacturer,
              aircraftType: it.aircraft_info?.aircraft_type,
              version: it.aircraft_info?.version,
              bodyType: it.aircraft_info?.body_type
            }),
            isFlightVia: it?.is_flight_via ?? false,
            isTransit: it?.is_transit ?? false,
            sequence: it.sequence
          })
      );
      return new CargoV2SearchFlight({
        uniqueId: item.unique_id ?? "",
        orderId: item.order_id ?? 0,
        departureDate: item.departure_date ?? "",
        arrivalDate: item.arrival_date ?? "",
        originAirportCode: item.origin_airport_code ?? "",
        destinationAirportCode: item.destination_airport_code ?? "",
        departureTime: item.departure_time ?? 0,
        arrivalTime: item.arrival_time ?? 0,
        transitNumber: item.transit_number ?? 0,
        isRecommended: Boolean(item.is_recommended),
        isDgAllowed: Boolean(item.is_dg_allowed),
        capacityAvailable: Boolean(item.capacity_available),
        dimensionAvailable: Boolean(item.dimension_available),
        isVolumeAvailability: Boolean(item.is_volume_availability),
        historyDepartureStatus: item.history_departure_status ?? "",
        embargo: Boolean(item.embargo),
        remainingCapacity: item.remaining_capacity ?? 0,
        totalTravelTimeMin: item.total_travel_time_min ?? 0,
        totalLeg: item.total_leg ?? 2,
        itineraries: itineraries,
        embargoReason: item.embargo_reason ?? "",
        isFlightVia: item.is_flight_via ?? false,
        isDirect: item.is_direct,
        isTransit: item.is_transit
      });
    });
    return new CargoV2SearchFlightResult({
      data: flights,
      totalFlight,
      departureDateStart: data.departure_date_start ?? "",
      departureDateEnd: data.departure_date_end ?? "",
      originAirports: data.origin_airports ?? [],
      destinationAirports: data.destination_airports ?? []
    });
  }
  public convertCargoAddFromApi(result: AxiosResponse): CargoAddResponse {
    const {
      data: { data }
    } = result;
    return new CargoAddResponse(
      data.cargo_no,
      data.total_stt_success,
      data.total_stt_failed,
      data.list_stt_failed,
      data.is_cargo_using_awb,
      data.booking_request_id
    );
  }

  public convertCreateCargoFromApi(result: AxiosResponse): CreateCargoV3 {
    const {
      data: { data }
    } = result;
    return new CreateCargoV3(convertSnakeToCamel(data));
  }

  public convertCargoCommodityFromApi(
    result: AxiosResponse
  ): CargoCommodityList {
    const { data } = result;
    const cargoCommodity: CargoCommodityData[] = [];
    for (const commodity of data.data) {
      cargoCommodity.push(
        new CargoCommodityData(
          commodity.commodity_group_id,
          commodity.commodity_group_code,
          commodity.commodity_group_name,
          commodity.created_at,
          commodity.updated_at
        )
      );
    }
    return new CargoCommodityList(
      new Pagination(data.meta.page, data.meta.limit),
      cargoCommodity
    );
  }

  public convertCalculateFromApi(res: AxiosResponse): CalculateData {
    const data = res.data;

    return new CalculateData({
      chargeableWeight: data.data.chargeable_weight,
      volumeWeight: data.data.volume_weight,
      height: data.data.height,
      isUploadProofPhoto: data.data.is_upload_proof_photo
    });
  }

  public convertCargoProductDataFromApi(
    res: AxiosResponse<any>
  ): CargoV2ProductData[] {
    return res.data.data?.map(
      (d: any) => new CargoV2ProductData(convertSnakeToCamel(d))
    );
  }

  public convertCargoHistoryFromApi(
    res: AxiosResponse<any>
  ): CargoV2HistoryData[] {
    return res.data.data?.map(
      (d: any) => new CargoV2HistoryData(convertSnakeToCamel(d))
    );
  }

  public convertAwbDataFromApi(result: AxiosResponse<any>): AwbDetailData {
    const {
      data: { data }
    } = result;
    return new AwbDetailData({
      awbDetails: data.awb_details.length
        ? data.awb_details.map(
            (item: any) =>
              new AwbDetail({
                awbNumber: item.awb_number,
                awbDate: item.awb_date,
                originCity: item.origin_city,
                originCityName: item.origin_city_name,
                destinationCity: item.destination_city,
                destinationCityName: item.destination_city_name,
                pieces: item.pieces,
                chargeableWeight: item.chargeable_weight,
                grossWeight: item.gross_weight,
                volumeWeight: item.volume_weight,
                product: item.product,
                commodity: item.commodity,
                originAirportCode: item.origin_airport_code,
                destinationAirportCode: item.destination_airport_code,
                originAirportName: item.origin_airport_name,
                destinationAirportName: item.destination_airport_name,
                isValidAwb: item.is_valid_awb,
                isValidOriginOrDestination: item.is_valid_origin_or_destination
              })
          )
        : [],
      transitRoute: data.transit_route,
      shc: data.shc,
      nog: data.nog,
      flightData: new FlightData({
        departureDate: data.flight_data.departure_date,
        arrivalDate: data.flight_data.arrival_date,
        originAirportCode: data.flight_data.origin_airport_code,
        destinationAirportCode: data.flight_data.destination_airport_code,
        departureTime: data.flight_data.departure_time,
        arrivalTime: data.flight_data.arrival_time,
        transitNumber: data.flight_data.transit_number,
        orderId: data.flight_data.order_id,
        isDgAllowed: data.flight_data.is_dg_allowed,
        capacityAvailable: data.flight_data.capacity_available,
        dimensionAvailable: data.flight_data.dimension_available,
        historyDepartureStatus: data.flight_data.history_departure_status,
        itineraries: data.flight_data.itineraries.length
          ? data.flight_data.itineraries.map(
              (item: any) =>
                new FlightData({
                  flightNo: item.flight_no,
                  departureDate: item.departure_date,
                  arrivalDate: item.arrival_date,
                  originAirportCode: item.origin_airport_code,
                  destinationAirportCode: item.destination_airport_code,
                  transitNumber: item.transit_number,
                  orderId: item.order_id
                })
            )
          : []
      })
    });
  }

  public convertCalculateVolumeFromApi(
    result: AxiosResponse<any>
  ): CalculateVolume {
    const {
      data: { data }
    } = result;
    return new CalculateVolume(convertSnakeToCamel(data));
  }

  public convertCargoTransportTypeFromApi(
    res: AxiosResponse
  ): CargoTransportType[] {
    const {
      data: { data }
    } = res;

    return Array.isArray(data)
      ? data.map(
          (ct: any) =>
            new CargoTransportType({
              transportLabel: ct.transport_label,
              transportType: ct.transport_type
            })
        )
      : [];
  }

  private createCargoV2RetryDetailStts(detailStt: any): CargoV2DetailStt[] {
    return detailStt
      ? detailStt.map(
          (stt: any) =>
            new CargoV2DetailStt({
              baggingNumber: stt.rcd_bag_no,
              baggingVendorNumber: stt.rcd_bag_vendor_no,
              sttNumber: stt.rcd_stt_no,
              totalPieces: stt.rcd_stt_total_piece,
              grossWeight: stt.rcd_stt_gross_wight,
              volumeWeight: stt.rcd_stt_volume_weight,
              productType: stt.rcd_stt_product_type,
              commodity: stt.stt_commodity_code,
              commodityGroupCode: stt.stt_commodity_group_code,
              commodityGroupName: stt.stt_commodity_group_name,
              commodityGroupIsDg: stt.stt_commodity_group_is_dg,
              commodityGroupIsQuarantine: stt.stt_commodity_group_is_quarantine,
              shcCode: stt.stt_commodity_shc_code,
              shcName: stt.stt_commodity_shc_description,
              origin: stt.rcd_stt_origin_city_id,
              destination: stt.rcd_stt_destination_city_id,
              destinationCityCode: stt.rcd_stt_destination_city_id,
              destinationCityName: stt.rcd_stt_destination_city_name,
              sttTotalPiece: stt.rcd_stt_total_piece,
              sttGrossWeight: stt.rcd_stt_gross_weight,
              sttVolumeWeight: stt.rcd_stt_volume_weight,
              sttProductType: stt.rcd_stt_product_type,
              sttCommodityName: stt.stt_commodity_name,
              sttOriginCityId: stt.rcd_stt_origin_city_id,
              sttDestinationCityId: stt.rcd_stt_destination_city_id,
              sttNoRefExternal: stt.rcd_stt_no_ref_external,
              isScanByBag: stt.is_scan_by_bag,
              bagData: new CargoV2DetailBagData({
                bagCustomGrossWeight: stt.rcd_bag_custom_gross_weight,
                bagCustomVolumeWeight: stt.rcd_bag_custom_volume_weight,
                bagCustomChargeableWeight: stt.rcd_bag_custom_chargeable_weight
              }),
              chargeableWeight: stt.rcd_stt_chargeable_weight,
              bagCustomChargeableWeight: stt.rcd_bag_custom_chargeable_weight,
              isDangerousGoods: stt.is_dangerous_goods,
              isQuarantine: stt.is_quarantine,
              shipmentId: stt.rcd_stt_shipment_id,
              totalSttChargeableWeight: stt.stt_chargeable_weight,
              sttClientId: stt.stt_client_id
            })
        )
      : [];
  }

  public convertRetryCargoDetailFromApi(
    result: AxiosResponse<any>
  ): CargoV2Data {
    const { data } = result.data;
    return this.createRetryCargoDataFromApiData(data);
  }

  private createRetryCargoDataFromApiData(data: any): CargoV2Data {
    return new CargoV2Data({
      id: data.rc_id,
      totalFlight: data.rc_total_flight,
      cargoNumber: data.rc_no,
      cargoHubName: data.rc_hub_name,
      driverName: data.rc_driver_name,
      estimationDepartureDate: data.rc_departure_date,
      estimationArrivalDate: data.rc_arrival_date,
      bookingRequestId: data.rc_booking_request_id,
      cargoType: data.rc_type,
      vehiclePoliceNumber: data.rc_vehicle_no,
      originCityCode: data.rc_origin_city_code,
      originCityName: data.rc_origin_city_name,
      destinationCityCode: data.rc_destination_city_code,
      destinationCityName: data.rc_destination_city_name,
      destinationCountryCode: data.destination_country_code || "",
      airportOriginCityCode: data.rc_origin_airport_code,
      airportOriginCityName: data.rc_origin_airport_name,
      airportDestinationCityCode: data.rc_destination_airport_code,
      airportDestinationCityName: data.rc_destination_city_name,
      originDistrictCode: "",
      destinationDistrictCode: "",
      totalStt: data.rc_total_stt,
      totalPieces: data.rc_total_stt_piece,
      totalGrossWeight: data.rc_total_gross_weight,
      totalVolumeWeight: data.rc_total_volume_weight,
      totalEstCargoGrossWeight: data.rc_total_estimate_gross_weight,
      totalEstCargoVolumeWeight: data.rc_total_estimate_volume_weight,
      totalActualCargoGrossWeight: data.rc_total_custom_gross_weight,
      totalActualCargoVolumeWeight: data.rc_total_custom_volume_weight,
      cargoActualPiece: data.rc_actual_piece,
      partnerName: data.rc_partner_name,
      partnerType: data.rc_partner_type,
      cargoCommodity: data.rc_commodity_group_name,
      cargoCommodityCode: data.rc_commodity_group_code,
      cargoProduct: data.rc_cargo_product_type_code,
      cargoProductCode: data.rc_cargo_product_code,
      shcCode: data.rc_shc_code,
      shcDescription: data.rc_shc_description,
      estimationDepartureData: data.rc_estimation_departure_data,
      departureDate: data.rc_departure_date,
      arrivalDate: data.rc_arrival_date || data.rc_aririval_date,
      createdAt: data.rc_created_at,
      createdBy: data.rc_created_name,
      updatedAt: data.rc_updated_at,
      updatedBy: data.rc_update_by,
      detailStt: this.createCargoV2RetryDetailStts(data.rc_detail),
      totalPiecesBagStt: data.rec_detail
        ? data.rec_detail.reduce((a: any, b: any) => a + b.TotalPiece, 0)
        : 0,
      totalPiecesDimension: this.calculateTotalPiecesDimension(
        this.mapCargoDimensionCustom(data.rc_cargo_dimension_custom)
      ),
      totalGrossDimension: this.calculateTotalGrossDimension(
        this.mapCargoDimensionCustom(data.rc_cargo_dimension_custom)
      ),
      actualDimension: new DimensionData({
        length: data.rc_actual_length_dimension,
        width: data.rc_actual_width_dimension,
        height: data.rc_actual_height_dimension
      }),
      estDimension: new DimensionData({
        length: data.rc_est_length_dimension,
        width: data.rc_est_width_dimension,
        height: data.rc_est_height_dimension
      }),
      nog: data.rc_nog,
      partnerId: data.rc_partner_id || 0,
      cargoStatus: data.rc_status,
      cargoDimensionCustom: this.mapCargoDimensionCustom(
        data.rc_cargo_dimension_custom
      ),
      isFromRtc: data.is_from_rtc,
      isPlaneManual: data.rc_is_cargo_plane_manual,
      isUseSabre: data.rc_is_use_sabre,
      isOverCutoffTime: data.is_over_cutoff_time,
      reasonFailed: data.rc_reason_failed,
      flightPlaneForECargo: data.flight_plan_for_ecargo,
      photoProof: data.photo_proof,
      isAllowUseProof: data.is_allow_use_proof,
      rcTotalChargeableWeight: data.rc_total_chargeable_weight,
      rcTotalCustomChargeableWeight: data.rc_total_custom_chargeable_weight,
      rcTotalEstimateChargeableWeight: data.rc_total_estimate_chargeable_weight,
      cargoTotalChargeableWeight: data.rc_total_chargeable_weight,
      cargoTotalCustomChargeableWeight: data.rc_total_custom_chargeable_weight,
      cargoTotalEstimateChargeableWeight:
        data.rc_total_estimate_chargeable_weight,
      cargoTotalCustomGrossWeight: data.rc_total_custom_gross_weight,
      cargoTotalCustomVolumeWeight: data.rc_total_custom_volume_weight,
      calculateType: data.calculate_type,
      isCargoDimensionOn: data.is_cargo_dimension_on,
      isValidAwbManual: data.is_valid_awb_manual,
      currentChargeableWeight: data.current_chargeable_weight
    });
  }

  public convertScanDataFromApi(result: AxiosResponse<any>): ScanDetail {
    const {
      data: { data }
    } = result;
    const converted = convertSnakeToCamel(data) as any;
    if (converted?.bagOrStt) {
      converted.bagOrStt = converted.bagOrStt.map((item: any) => ({
        ...item,
        sttWoodPacking: getScanFlags(item).isWoodpacking,
        sttDetails: (item.sttDetails ?? []).map((detail: any) => ({
          ...detail,
          sttWoodPacking: getScanFlags(detail).isWoodpacking
        }))
      }));
    }
    return new ScanDetail(converted);
  }
}
